import React, { useEffect, useMemo, useState } from 'react';
import type { Time } from 'lightweight-charts';
import {
  Speedometer2, GraphUpArrow, Trophy, ListCheck, ChevronDoubleLeft, ChevronDoubleRight, MoonStars, Sun,
} from 'react-bootstrap-icons';
import TvChart, { kstEpoch, type Bar, type ChartTheme } from './TvChart';
import { getMainAxios } from '../utils/util';
import { useAuth } from '../context/AuthContext';
import { won, pct } from '../styles/fdsTheme';

/**
 * 투자 플랫폼 다크 대시보드 샘플 (2026-11-03)
 *
 * 참고: Converge 'Dark Investment Platform Dashboard' 템플릿 소개문의 구성 요소
 *   - 접히는 사이드바 (펼침: 아이콘+글자 / 접힘: 아이콘만)
 *   - 상단에 흐르는 시세 티커 바
 *   - KPI 카드 · 펀드(=전략 슬롯) 랭킹 · 시장 · 주문 4페이지
 *   - CSS 변수(custom property) 한 벌로 다크/라이트 전환
 * 원본 디자인 파일은 열람 불가라 소개문 기준으로 재구성했다. 색은 아래 THEMES 가 유일한 원천.
 *
 * 상단 앱 메뉴는 그대로 두고, 이 화면 안에서만 사이드바로 페이지를 바꾼다.
 * 데이터는 DB 조회 API 만 — KIS 호출·발주 없음.
 */

type PageId = 'overview' | 'market' | 'ranking' | 'orders';
const PAGES: { id: PageId; label: string; Icon: React.ComponentType<{ size?: number }> }[] = [
  { id: 'overview', label: '개요', Icon: Speedometer2 },
  { id: 'market', label: '시장', Icon: GraphUpArrow },
  { id: 'ranking', label: '전략 랭킹', Icon: Trophy },
  { id: 'orders', label: '주문', Icon: ListCheck },
];

// 테마 토큰 — CSS 변수로 내보내고, 캔버스(차트)에는 같은 값을 직접 넘긴다
const THEMES = {
  dark: {
    bg: '#0a0e17', panel: '#111827', panel2: '#0f1623', border: '#1f2937', text: '#e5e7eb', muted: '#9ca3af',
    hint: '#6b7280', accent: '#10b981', accent2: '#f59e0b', up: '#f87171', down: '#60a5fa', flat: '#d1d5db',
    upBg: 'rgba(248,113,113,0.14)', downBg: 'rgba(96,165,250,0.14)', glow: '0 0 0 1px rgba(16,185,129,0.25)',
  },
  light: {
    bg: '#f4f6fa', panel: '#ffffff', panel2: '#f8fafc', border: '#e5e7eb', text: '#111827', muted: '#6b7280',
    hint: '#9ca3af', accent: '#059669', accent2: '#d97706', up: '#dc2626', down: '#2563eb', flat: '#374151',
    upBg: '#fee2e2', downBg: '#dbeafe', glow: '0 1px 2px rgba(15,23,42,0.06)',
  },
} as const;
type Tok = { [K in keyof typeof THEMES.dark]: string };

const cssVars = (t: Tok): React.CSSProperties =>
  Object.fromEntries(Object.entries(t).map(([k, v]) => [`--dip-${k}`, v])) as React.CSSProperties;

const CSS = `
.dip { background: var(--dip-bg); color: var(--dip-text); min-height: 100vh; display: flex; font-size: 12px; }
.dip-side { background: var(--dip-panel); border-right: 1px solid var(--dip-border); display: flex; flex-direction: column;
  transition: width .2s; flex-shrink: 0; position: sticky; top: 0; height: 100vh; }
.dip-nav { display: flex; align-items: center; gap: 10px; padding: 10px 14px; margin: 2px 8px; border-radius: 8px; cursor: pointer;
  color: var(--dip-muted); background: none; border: none; font-size: 13px; text-align: left; white-space: nowrap; }
.dip-nav:hover { background: var(--dip-panel2); color: var(--dip-text); }
.dip-nav.on { background: var(--dip-upBg); background: rgba(16,185,129,0.12); color: var(--dip-accent); font-weight: 600; }
.dip-nav:focus-visible, .dip-btn:focus-visible { outline: 2px solid var(--dip-accent); outline-offset: 2px; }
.dip-main { flex: 1; min-width: 0; display: flex; flex-direction: column; }
.dip-ticker { overflow: hidden; border-bottom: 1px solid var(--dip-border); background: var(--dip-panel2); white-space: nowrap; }
.dip-ticker-track { display: inline-flex; gap: 28px; padding: 8px 0; animation: dip-scroll 40s linear infinite; }
.dip-ticker:hover .dip-ticker-track { animation-play-state: paused; }
@keyframes dip-scroll { from { transform: translateX(0); } to { transform: translateX(-50%); } }
@media (prefers-reduced-motion: reduce) { .dip-ticker-track { animation: none; } }
.dip-body { padding: 20px; display: grid; grid-template-columns: repeat(12, minmax(0,1fr)); gap: 16px; }
.dip-card { background: var(--dip-panel); border: 1px solid var(--dip-border); border-radius: 12px; padding: 16px; min-width: 0;
  box-shadow: var(--dip-glow); }
.c3 { grid-column: span 3; } .c4 { grid-column: span 4; } .c5 { grid-column: span 5; } .c7 { grid-column: span 7; }
.c8 { grid-column: span 8; } .c12 { grid-column: span 12; }
@media (max-width: 1100px) { .c3 { grid-column: span 6; } .c4, .c5, .c7, .c8 { grid-column: span 12; } }
@media (max-width: 640px)  { .c3 { grid-column: span 12; } .dip-body { padding: 12px; } .dip-side { display: none; } .dip-mnav { display: flex !important; } }
.dip-h { font-size: 12px; font-weight: 600; color: var(--dip-muted); text-transform: none; letter-spacing: .02em; margin-bottom: 10px;
  display: flex; justify-content: space-between; align-items: center; gap: 8px; }
.dip-big { font-size: 22px; font-weight: 700; font-variant-numeric: tabular-nums; line-height: 1.2; }
.dip-t { width: 100%; border-collapse: collapse; font-variant-numeric: tabular-nums; }
.dip-t th { color: var(--dip-hint); font-weight: 600; font-size: 11px; text-align: right; padding: 6px 8px; border-bottom: 1px solid var(--dip-border); }
.dip-t td { text-align: right; padding: 7px 8px; border-bottom: 1px solid var(--dip-border); }
.dip-t th:first-child, .dip-t td:first-child { text-align: left; }
.dip-t tr.click { cursor: pointer; } .dip-t tr.click:hover td { background: var(--dip-panel2); }
.dip-btn { font-size: 12px; padding: 4px 10px; border-radius: 999px; cursor: pointer; border: 1px solid var(--dip-border);
  background: var(--dip-panel2); color: var(--dip-text); display: inline-flex; align-items: center; gap: 6px; }
.dip-btn.on { background: var(--dip-accent); border-color: var(--dip-accent); color: #fff; }
.dip-empty { color: var(--dip-hint); padding: 12px 0; }
.dip-badge { font-size: 11px; font-weight: 600; padding: 1px 6px; border-radius: 4px; }
`;

const fmtDate = (d: Date) =>
  `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, '0')}-${String(d.getDate()).padStart(2, '0')}`;
const daysAgo = (n: number) => fmtDate(new Date(Date.now() - n * 86400000));
const arr = (d: any): any[] => (Array.isArray(d) ? d : Array.isArray(d?.data) ? d.data : []);

const store = {
  get: (k: string) => { try { return localStorage.getItem(k); } catch { return null; } },
  set: (k: string, v: string) => { try { localStorage.setItem(k, v); } catch { /* 저장 못 해도 동작 */ } },
};

interface Quote { prodId: string; prodNm: string; last?: number; chg?: number; }

/** 작은 추세선 (누적손익 등) */
const Spark: React.FC<{ values: number[]; color: string; w?: number; h?: number }> = ({ values, color, w = 90, h = 24 }) => {
  if (values.length < 2) return <span style={{ color: 'var(--dip-hint)' }}>-</span>;
  const min = Math.min(...values), max = Math.max(...values), rng = max - min || 1;
  const pts = values.map((v, i) => `${(i / (values.length - 1)) * w},${h - ((v - min) / rng) * (h - 2) - 1}`).join(' ');
  return <svg width={w} height={h} aria-hidden><polyline points={pts} fill="none" stroke={color} strokeWidth={1.5} /></svg>;
};

const DarkInvestDashboard: React.FC = () => {
  const axios = getMainAxios();
  const { selectedPfId } = useAuth();
  const [mode, setMode] = useState<'dark' | 'light'>(() => (store.get('fds_dipTheme') === 'light' ? 'light' : 'dark'));
  const [collapsed, setCollapsed] = useState(() => store.get('fds_dipSide') === '1');
  const [pageId, setPageId] = useState<PageId>('overview');
  const t: Tok = THEMES[mode];
  const sign = (v?: number | null) => ((v ?? 0) > 0 ? t.up : (v ?? 0) < 0 ? t.down : t.flat);
  const chartTheme: ChartTheme = useMemo(() => ({ bg: t.panel, text: t.muted, grid: t.border, border: t.border }), [t]);

  const [bs, setBs] = useState<any[]>([]);
  const [positions, setPositions] = useState<any[]>([]);
  const [orders, setOrders] = useState<any[]>([]);
  const [quotes, setQuotes] = useState<Quote[]>([]);
  const [rise, setRise] = useState<any[]>([]);
  const [slots, setSlots] = useState<any[]>([]);
  const [daily, setDaily] = useState<{ d: string; bySlot: Record<string, number> }[]>([]);
  const [prodId, setProdId] = useState('');
  const [period, setPeriod] = useState<'D' | '5m'>('D');
  const [bars, setBars] = useState<Bar[]>([]);

  // 계좌 데이터
  useEffect(() => {
    if (!selectedPfId) return;
    const d = fmtDate(new Date());
    axios.get('/api/v1/pfbs/recent', { params: { pfId: selectedPfId, d } }).then(r => setBs(arr(r.data))).catch(() => setBs([]));
    axios.get('/api/v1/portfolio/positions', { params: { id: selectedPfId, d } }).then(r => setPositions(arr(r.data))).catch(() => setPositions([]));
    axios.get('/api/v1/portfolio/orders', { params: { id: selectedPfId, d0: d, d1: d } }).then(r => setOrders(arr(r.data))).catch(() => setOrders([]));
    axios.get('/api/v1/pfstrat/summary', { params: { pfId: selectedPfId, endD: d, days: 10 } })
      .then(r => { setSlots(r.data?.slots ?? []); setDaily(r.data?.daily ?? []); })
      .catch(() => { setSlots([]); setDaily([]); });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [selectedPfId]);

  // 관심종목 시세 + 상승률 상위 (티커·시장 페이지)
  useEffect(() => {
    (async () => {
      try {
        const groups = arr((await axios.get('/api/v1/watchlist/groups')).data);
        if (!groups.length) return;
        const items = arr((await axios.get(`/api/v1/watchlist/groups/${groups[0].id}/items`)).data);
        const ids: string[] = items.map((i: any) => i.prodId).filter((id: string) => /^[0-9Q]/.test(id));
        if (!ids.length) return;
        const nmRes = (await axios.get(`/api/v1/stocks/prod-nms?${ids.map(id => `prodIds=${encodeURIComponent(id)}`).join('&')}`)).data;
        const nms: Record<string, string> = nmRes?.data ?? nmRes ?? {};
        setProdId(p => p || ids[0]);
        setQuotes(await Promise.all(ids.map(async id => {
          const q: Quote = { prodId: id, prodNm: nms[id] || id };
          try {
            const h = arr((await axios.get('/api/v1/quant/screener/history', { params: { prodId: id } })).data);
            const n = h.length;
            if (n >= 2) { q.last = h[n - 1].c; q.chg = h[n - 2].c ? (h[n - 1].c / h[n - 2].c - 1) * 100 : 0; }
          } catch { /* 가격 없이 */ }
          return q;
        })));
      } catch { setQuotes([]); }
    })();
    axios.get('/api/v1/stocks/top-rise-rate', { params: { d: fmtDate(new Date()) } })
      .then(r => setRise(arr(r.data).slice(0, 10))).catch(() => setRise([]));
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  // 차트
  useEffect(() => {
    if (!prodId) return;
    (async () => {
      try {
        if (period === '5m') {
          const r = await axios.get('/api/v1/quant/intraday-bars', { params: { prodId, from: daysAgo(7), to: fmtDate(new Date()), minute: 5 } });
          setBars(arr(r.data).map((b: any) => ({ time: kstEpoch(String(b.d), String(b.t)), open: b.o, high: b.h, low: b.l, close: b.c, volume: b.v })));
        } else {
          const r = await axios.get('/api/v1/quant/screener/history', { params: { prodId } });
          setBars(arr(r.data).map((b: any) => ({ time: b.date as Time, open: b.o, high: b.h, low: b.l, close: b.c, volume: b.v })));
        }
      } catch { setBars([]); }
    })();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [prodId, period]);

  // ── 파생값 ──
  const bsSorted = useMemo(() => [...bs].sort((a, b) => String(a.d).localeCompare(String(b.d))), [bs]);
  const today = bsSorted[bsSorted.length - 1];
  const yday = bsSorted[bsSorted.length - 2];
  const dayDiff = today && yday ? today.totEvluAmt - yday.totEvluAmt : today?.diffAmt;
  const held = positions.filter(p => p.pos);
  const evalSum = held.reduce((s, p) => s + (p.evluamt ?? 0), 0);
  const prftSum = held.reduce((s, p) => s + (p.prft ?? 0), 0);
  const filled = orders.filter(o => o.state === 30 && !o.errMsg && o.cumQty).length;
  const ranked = useMemo(() => {
    const ds = [...daily].sort((a, b) => a.d.localeCompare(b.d));
    return slots.map(s => {
      let acc = 0;
      const curve = ds.map(x => (acc += x.bySlot?.[String(s.id)] ?? 0));
      const evalAmt = ((s.holdings ?? []) as { pos: number; px: number }[]).reduce((a, h) => a + h.pos * h.px, 0);
      return { id: s.id, name: s.name, total: s.total ?? 0, retPct: s.retPct as number | null, evalAmt, curve };
    }).sort((a, b) => b.total - a.total);
  }, [slots, daily]);
  const cur = quotes.find(q => q.prodId === prodId);
  const tickerItems = [...quotes, ...rise.map(r => ({ prodId: r.prodId, prodNm: r.prodNm ?? r.prodId, last: r.clsprc, chg: r.flurt }))];

  const orderState = (o: any) => (o.errMsg ? '거부' : o.state === 30 ? (o.cumQty ? '완료' : '미체결') : o.cnclT ? '취소' : '진행');
  const isBuy = (o: any) => (o.ls ?? 1) * (o.ex ?? 1) > 0;   // sgn = ls×ex

  const toggleMode = () => setMode(m => { const n = m === 'dark' ? 'light' : 'dark'; store.set('fds_dipTheme', n); return n; });
  const toggleSide = () => setCollapsed(c => { store.set('fds_dipSide', c ? '0' : '1'); return !c; });

  // ── 조각 ──
  const Kpi = ({ title, value, color, sub }: { title: string; value: string; color?: string; sub?: React.ReactNode }) => (
    <div className="dip-card c3">
      <div className="dip-h">{title}</div>
      <div className="dip-big" style={{ color: color ?? t.text }}>{value}</div>
      {sub != null && <div style={{ marginTop: 6, color: t.muted, fontVariantNumeric: 'tabular-nums' }}>{sub}</div>}
    </div>
  );

  const ChartCard = ({ span }: { span: string }) => (
    <div className={`dip-card ${span}`}>
      <div className="dip-h">
        <span>
          <span style={{ color: t.text, fontSize: 13 }}>{cur?.prodNm ?? prodId}</span> <span style={{ color: t.hint, fontWeight: 400 }}>{prodId}</span>
          {cur?.last != null && <span style={{ marginLeft: 10, color: sign(cur.chg), fontVariantNumeric: 'tabular-nums' }}>{won(cur.last)} {pct(cur.chg)}</span>}
        </span>
        <span style={{ display: 'flex', gap: 4 }}>
          <button className={`dip-btn ${period === '5m' ? 'on' : ''}`} onClick={() => setPeriod('5m')}>5분</button>
          <button className={`dip-btn ${period === 'D' ? 'on' : ''}`} onClick={() => setPeriod('D')}>일</button>
        </span>
      </div>
      {bars.length ? <TvChart bars={bars} height={360} intraday={period === '5m'} theme={chartTheme} />
        : <div className="dip-empty" style={{ height: 360 }}>차트 데이터가 없습니다</div>}
    </div>
  );

  const QuoteTable = ({ rows, title }: { rows: Quote[]; title: string }) => (
    <>
      <div className="dip-h">{title}</div>
      {rows.length === 0 ? <div className="dip-empty">데이터가 없습니다</div> : (
        <table className="dip-t"><thead><tr><th>종목</th><th>현재가</th><th>등락률</th></tr></thead>
          <tbody>{rows.map(q => (
            <tr key={q.prodId} className="click" onClick={() => setProdId(q.prodId)}
              style={q.prodId === prodId ? { boxShadow: `inset 3px 0 0 ${t.accent}` } : undefined}>
              <td><b style={{ color: t.text }}>{q.prodNm}</b> <span style={{ color: t.hint }}>{q.prodId}</span></td>
              <td>{won(q.last)}</td>
              <td><span className="dip-badge" style={{ color: sign(q.chg), background: (q.chg ?? 0) >= 0 ? t.upBg : t.downBg }}>{pct(q.chg)}</span></td>
            </tr>
          ))}</tbody></table>
      )}
    </>
  );

  const HoldTable = () => held.length === 0 ? <div className="dip-empty">보유 종목이 없습니다</div> : (
    <table className="dip-t"><thead><tr><th>종목</th><th>수량</th><th>평가금액</th><th>비중</th><th>손익</th></tr></thead>
      <tbody>{[...held].sort((a, b) => (b.evluamt ?? 0) - (a.evluamt ?? 0)).map(p => {
        const id = p.prodid ?? p.prodId;
        const w = evalSum ? (p.evluamt ?? 0) / evalSum * 100 : 0;
        return (
          <tr key={id} className="click" onClick={() => setProdId(id)}>
            <td><b style={{ color: t.text }}>{p.prodNm ?? id}</b></td>
            <td>{won(p.pos)}</td><td>{won(p.evluamt)}</td>
            <td style={{ whiteSpace: 'nowrap' }}>
              <span style={{ display: 'inline-block', width: 40, height: 4, borderRadius: 2, background: t.border, verticalAlign: 'middle', marginRight: 6 }}>
                <span style={{ display: 'block', width: `${w}%`, height: '100%', borderRadius: 2, background: t.accent }} />
              </span>{w.toFixed(1)}%
            </td>
            <td style={{ color: sign(p.prft) }}>{won(p.prft)}</td>
          </tr>
        );
      })}</tbody></table>
  );

  const RankTable = ({ limit }: { limit?: number }) => ranked.length === 0 ? <div className="dip-empty">전략 슬롯 성과가 없습니다</div> : (
    <table className="dip-t"><thead><tr><th>#  전략</th><th>누적손익 (10일)</th><th>수익률</th><th>평가금액</th><th>추이</th></tr></thead>
      <tbody>{ranked.slice(0, limit ?? ranked.length).map((r, i) => (
        <tr key={r.id}>
          <td>
            <span style={{ display: 'inline-block', width: 20, color: i < 3 ? t.accent2 : t.hint, fontWeight: 700 }}>{i + 1}</span>
            <b style={{ color: t.text }}>{r.name}</b>
          </td>
          <td style={{ color: sign(r.total) }}>{won(r.total)}</td>
          <td style={{ color: sign(r.retPct) }}>{r.retPct != null ? pct(r.retPct) : '-'}</td>
          <td>{won(r.evalAmt)}</td>
          <td><Spark values={r.curve} color={r.total > 0 ? t.up : r.total < 0 ? t.down : t.hint} /></td>
        </tr>
      ))}</tbody></table>
  );

  const OrderTable = ({ limit }: { limit?: number }) => orders.length === 0 ? <div className="dip-empty">오늘 주문이 없습니다</div> : (
    <table className="dip-t"><thead><tr><th>종목</th><th>구분</th><th>시각</th><th>체결/주문</th><th>평균가</th><th>전략</th><th>상태</th></tr></thead>
      <tbody>{[...orders].reverse().slice(0, limit ?? orders.length).map(o => {
        const b = isBuy(o), st = orderState(o);
        return (
          <tr key={o.id}>
            <td><b style={{ color: t.text }}>{o.prodNm ?? o.prodId}</b></td>
            <td><span className="dip-badge" style={{ color: b ? t.up : t.down, background: b ? t.upBg : t.downBg }}>{b ? '매수' : '매도'}</span></td>
            <td style={{ color: t.muted }}>{o.hm0 ?? ''}</td>
            <td>{won(o.cumQty)}/{won(o.qty2ord ?? o.qty2Ord)}</td>
            <td>{o.avgPx ? won(o.avgPx) : '-'}</td>
            <td style={{ color: t.muted }}>{o.slotName ?? '-'}</td>
            <td style={{ color: st === '거부' ? t.up : st === '완료' ? t.accent : t.muted }}>{st}</td>
          </tr>
        );
      })}</tbody></table>
  );

  return (
    <div className="dip" style={cssVars(t)}>
      <style>{CSS}</style>

      {/* 사이드바 */}
      <aside className="dip-side" style={{ width: collapsed ? 60 : 190 }}>
        <div style={{ padding: '16px 14px', display: 'flex', alignItems: 'center', gap: 8, borderBottom: `1px solid ${t.border}` }}>
          <span style={{ width: 28, height: 28, borderRadius: 8, background: t.accent, color: '#fff', display: 'grid', placeItems: 'center', fontWeight: 800, flexShrink: 0 }}>F</span>
          {!collapsed && <b style={{ fontSize: 14, whiteSpace: 'nowrap' }}>FDS Invest</b>}
        </div>
        <nav style={{ paddingTop: 8, flex: 1 }}>
          {PAGES.map(({ id, label, Icon }) => (
            <button key={id} className={`dip-nav ${pageId === id ? 'on' : ''}`} onClick={() => setPageId(id)}
              title={collapsed ? label : undefined} aria-current={pageId === id ? 'page' : undefined}
              style={{ width: collapsed ? 44 : 'calc(100% - 16px)', justifyContent: collapsed ? 'center' : 'flex-start', padding: collapsed ? '10px 0' : undefined }}>
              <Icon size={16} />{!collapsed && label}
            </button>
          ))}
        </nav>
        <div style={{ padding: 8, borderTop: `1px solid ${t.border}`, display: 'flex', flexDirection: collapsed ? 'column' : 'row', gap: 6 }}>
          <button className="dip-btn" onClick={toggleMode} title="테마 전환" aria-label="테마 전환">
            {mode === 'dark' ? <MoonStars size={14} /> : <Sun size={14} />}{!collapsed && (mode === 'dark' ? '다크' : '라이트')}
          </button>
          <button className="dip-btn" onClick={toggleSide} title={collapsed ? '펼치기' : '접기'} aria-label={collapsed ? '사이드바 펼치기' : '사이드바 접기'}>
            {collapsed ? <ChevronDoubleRight size={14} /> : <ChevronDoubleLeft size={14} />}
          </button>
        </div>
      </aside>

      <div className="dip-main">
        {/* 티커 바 — 마우스를 올리면 멈춘다. 같은 목록을 두 번 이어 붙여 끊김 없이 흐른다 */}
        <div className="dip-ticker" aria-label="시세 티커">
          {tickerItems.length === 0 ? <div style={{ padding: 8, color: t.hint }}>시세 불러오는 중...</div> : (
            <div className="dip-ticker-track">
              {[...tickerItems, ...tickerItems].map((q, i) => (
                <span key={i} style={{ cursor: 'pointer', fontVariantNumeric: 'tabular-nums' }} onClick={() => setProdId(q.prodId)}>
                  <b style={{ color: t.text }}>{q.prodNm}</b>{' '}
                  <span style={{ color: t.muted }}>{won(q.last)}</span>{' '}
                  <span style={{ color: sign(q.chg) }}>{(q.chg ?? 0) >= 0 ? '▲' : '▼'} {pct(q.chg)}</span>
                </span>
              ))}
            </div>
          )}
        </div>

        {/* 머리 + 폰 전용 페이지 선택 */}
        <div style={{ padding: '16px 20px 0', display: 'flex', justifyContent: 'space-between', alignItems: 'center', gap: 8, flexWrap: 'wrap' }}>
          <div>
            <div style={{ fontSize: 16, fontWeight: 700 }}>{PAGES.find(p => p.id === pageId)!.label}
              <span style={{ fontSize: 12, fontWeight: 400, color: t.hint, marginLeft: 8 }}>다크 투자 대시보드 샘플</span></div>
            <div style={{ color: t.muted }}>계좌 {selectedPfId ?? '-'} · {fmtDate(new Date())}</div>
          </div>
          <div className="dip-mnav" style={{ display: 'none', gap: 4, flexWrap: 'wrap' }}>
            {PAGES.map(p => <button key={p.id} className={`dip-btn ${pageId === p.id ? 'on' : ''}`} onClick={() => setPageId(p.id)}>{p.label}</button>)}
            <button className="dip-btn" onClick={toggleMode} aria-label="테마 전환">{mode === 'dark' ? <MoonStars size={12} /> : <Sun size={12} />}</button>
          </div>
        </div>

        <div className="dip-body">
          {pageId === 'overview' && <>
            {Kpi({ title: '총 평가금액', value: today?.totEvluAmt != null ? `${won(today.totEvluAmt)}원` : '-',
              sub: <span style={{ color: sign(dayDiff) }}>전일 대비 {won(dayDiff)}</span> })}
            {Kpi({ title: '오늘 실현손익', value: today?.tradeNet != null ? `${won(today.tradeNet)}원` : '-', color: sign(today?.tradeNet), sub: '비용 차감 후' })}
            {Kpi({ title: '보유 평가손익', value: `${won(prftSum)}원`, color: sign(prftSum), sub: `${held.length}종목 · 평가 ${won(evalSum)}원` })}
            {Kpi({ title: '오늘 주문 / 체결', value: `${orders.length} / ${filled}`, sub: `전략 슬롯 ${ranked.length}개` })}
            {ChartCard({ span: 'c8' })}
            <div className="dip-card c4"><div className="dip-h">보유 종목</div>{HoldTable()}</div>
            <div className="dip-card c7"><div className="dip-h">전략 랭킹 <span style={{ color: t.hint, fontWeight: 400 }}>TOP 5</span></div>{RankTable({ limit: 5 })}</div>
            <div className="dip-card c5"><div className="dip-h">최근 주문</div>{OrderTable({ limit: 6 })}</div>
          </>}

          {pageId === 'market' && <>
            {ChartCard({ span: 'c8' })}
            <div className="dip-card c4">{QuoteTable({ rows: quotes, title: '관심종목' })}</div>
            <div className="dip-card c12">{QuoteTable({ rows: rise.map(r => ({ prodId: r.prodId, prodNm: r.prodNm ?? r.prodId, last: r.clsprc, chg: r.flurt })), title: '상승률 상위 (오늘)' })}</div>
          </>}

          {pageId === 'ranking' && (
            <div className="dip-card c12">
              <div className="dip-h">전략 슬롯 랭킹 <span style={{ color: t.hint, fontWeight: 400 }}>최근 10영업일 누적 · 수수료·세금 추정 차감</span></div>
              {RankTable({})}
            </div>
          )}

          {pageId === 'orders' && (
            <div className="dip-card c12"><div className="dip-h">오늘 주문 <span style={{ color: t.hint, fontWeight: 400 }}>{orders.length}건</span></div>{OrderTable({})}</div>
          )}
        </div>
      </div>
    </div>
  );
};

export default DarkInvestDashboard;
